import React, { useState, useEffect } from "react";
import { ApiKeyOrGetter } from "../../../../services/GeneralService";
import { IntentService } from "../../../../services/IntentService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { DatasetData } from "../../../../model_gen/dataset";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { Badge } from "../../../ui/badge";
import { 
  CheckCircleIcon,
  DatabaseIcon,
  CloudUploadIcon,
  FileTextIcon,
  PercentIcon,
  TagIcon
} from "lucide-react";
import { cn } from "../../../../lib/utils";
import { useTagConfig } from "../../../../hooks/useTagConfig";

interface DatasetReviewProps {
  datasetData: DatasetData;
  apiKey: ApiKeyOrGetter;
}

const DatasetReview: React.FC<DatasetReviewProps> = ({
  datasetData,
  apiKey,
}) => {
  const { getTagDisplayConfig } = useTagConfig(apiKey);
  const [loading, setLoading] = useState<boolean>(false);
  const [baseTypeLabel, setBaseTypeLabel] = useState<string>("");
  const [intentGroupName, setIntentGroupName] = useState<string>("");
  const [applicationName, setApplicationName] = useState<string>("");
  const [intentName, setIntentName] = useState<string>("");

  const meta: any = datasetData.meta || {};
  const datasourceType = meta.datasource_type || meta.data_source_type || "production";

  // Check if this is a manual upload / S3 import
  const isManualUpload = datasourceType === "manual";
  const isS3Import = datasourceType === "s3";
  const manualDatasetSize = datasetData.meta?.manual_dataset_size as number;
  const uploadedFile = datasetData.meta?.uploaded_file as File;
  const s3Url = meta.s3_url as string | undefined;

  useEffect(() => {
    if (
      datasetData.base_type === "intent_group" &&
      datasetData.intent_group_id
    ) {
      setLoading(true);
      IntentService.getIntentGroup(
        datasetData.intent_group_id,
        false,
        setLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          const name =
            response.intent_name ||
            response.name ||
            `Intent Group ${datasetData.intent_group_id}`;
          setBaseTypeLabel("Intent Group");
          setIntentGroupName(name);
          setLoading(false);
        },
        (error: any) => {
          console.error("Error fetching intent group:", error);
          setBaseTypeLabel("Intent Group");
          setIntentGroupName(`Intent Group ${datasetData.intent_group_id}`);
          setLoading(false);
        }
      );
    } else if (
      datasetData.base_type === "application_intent" &&
      datasetData.application_id &&
      datasetData.intent_id
    ) {
      setLoading(true);
      ApplicationService.getApplication(
        datasetData.application_id,
        setLoading,
        apiKey
      ).subscribe(
        (appResponse: any) => {
          const appName =
            appResponse?.application_name ||
            appResponse?.name ||
            `Application ${datasetData.application_id}`;
          setApplicationName(appName);

          ApplicationService.getApplicationAction(
            datasetData.application_id,
            datasetData.intent_id,
            setLoading,
            apiKey
          ).subscribe(
            (intentResponse: any) => {
              const intName =
                intentResponse?.action_type ||
                `Intent ${datasetData.intent_id}`;
              setBaseTypeLabel("Application/Intent");
              setIntentName(intName);
              setLoading(false);
            },
            (error: any) => {
              console.error("Error fetching intent:", error);
              setBaseTypeLabel("Application/Intent");
              setIntentName(`Intent ${datasetData.intent_id}`);
              setLoading(false);
            }
          );
        },
        (error: any) => {
          console.error("Error fetching application:", error);
          setBaseTypeLabel("Application/Intent");
          setApplicationName(`Application ${datasetData.application_id}`);
          setIntentName(`Intent ${datasetData.intent_id}`);
          setLoading(false);
        }
      );
    }
  }, [datasetData, apiKey]);

  const getSampleTypeLabel = () => {
    if (isManualUpload) {
      return "Manual file upload";
    }
    if (isS3Import) {
      return "Import from S3";
    }

    switch (datasetData.sample_type) {
      case "all":
        return "All available requests";
      case "random":
        return `Random sample (${datasetData.sample_percentage}%)`;
      case "none":
        return "None (Empty dataset)";
      default:
        return "Not specified";
    }
  };

  const getDatasetSize = () => {
    if (isManualUpload) {
      return `${manualDatasetSize || 0} requests`;
    }
    if (isS3Import) {
      return "Import will run after dataset creation";
    }

    if (!datasetData.total_requests) return "0 requests";

    if (datasetData.sample_type === "all") {
      return `${datasetData.total_requests} requests`;
    } else if (
      datasetData.sample_type === "random" &&
      datasetData.sample_percentage
    ) {
      const size = Math.round(
        (datasetData.total_requests * datasetData.sample_percentage) / 100
      );
      return `${size} requests (${datasetData.sample_percentage}% of ${datasetData.total_requests})`;
    } else {
      return "0 requests";
    }
  };

  const getBaseTypeValue = () => {
    if (loading) return <Skeleton className="h-4 w-32" />;

    if (datasetData.base_type === "intent_group") {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-maitai-green/10 text-maitai-green">
              {baseTypeLabel}
            </span>
          </div>
          <div className="text-sm text-foreground font-gc-reg">{intentGroupName}</div>
        </div>
      );
    } else {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-maitai-green/10 text-maitai-green">
              {baseTypeLabel}
            </span>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-foreground font-gc-reg">Application: {applicationName}</div>
            <div className="text-sm text-foreground font-gc-reg">Intent: {intentName}</div>
          </div>
        </div>
      );
    }
  };

  const getDataSourceValue = () => {
    const label =
      datasourceType === "manual"
        ? "Manual Upload"
        : datasourceType === "s3"
          ? "S3 Import"
          : "Production Data";
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
              datasourceType === "manual" || datasourceType === "s3"
                ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                : "bg-maitai-green/10 text-maitai-green"
            )}
          >
            {label}
          </span>
        </div>
        {isManualUpload && uploadedFile && (
          <div className="text-sm text-muted-foreground">
            {uploadedFile.name} ({(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB)
          </div>
        )}
        {isS3Import && s3Url && (
          <div className="text-sm text-muted-foreground font-mono truncate">
            {s3Url}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Review Dataset
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Review your dataset configuration before creating
        </p>
      </div>

      {/* Summary Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <FileTextIcon className="w-5 h-5 text-maitai-green" />
            Dataset Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <div className="text-sm font-gc-med text-foreground">Name</div>
            <div className="text-sm text-foreground font-gc-reg">
              {datasetData.name || "Unnamed Dataset"}
            </div>
          </div>

          {/* Description */}
          {datasetData.description && (
            <div className="space-y-2">
              <div className="text-sm font-gc-med text-foreground">Description</div>
              <div className="text-sm text-foreground font-gc-reg">
                {datasetData.description}
              </div>
            </div>
          )}

          {/* Base Type */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <DatabaseIcon className="w-4 h-4 text-maitai-green" />
              <div className="text-sm font-gc-med text-foreground">Base Type</div>
            </div>
            {getBaseTypeValue()}
          </div>

          {/* Data Source */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CloudUploadIcon className="w-4 h-4 text-maitai-green" />
              <div className="text-sm font-gc-med text-foreground">Data Source</div>
            </div>
            {getDataSourceValue()}
          </div>

          {/* Sampling Method - only for production data (not manual uploads or S3 imports) */}
          {!isManualUpload && !isS3Import && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <PercentIcon className="w-4 h-4 text-maitai-green" />
                <div className="text-sm font-gc-med text-foreground">Sampling Method</div>
              </div>
              <div className="text-sm text-foreground font-gc-reg">
                {getSampleTypeLabel()}
              </div>
            </div>
          )}

          {/* Dataset Size */}
          <div className="space-y-2">
            <div className="text-sm font-gc-med text-foreground">
              {isManualUpload ? "Dataset Size" : "Estimated Size"}
            </div>
            <div className="text-sm text-maitai-green font-gc-med">
              {getDatasetSize()}
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TagIcon className="w-4 h-4 text-maitai-green" />
              <div className="text-sm font-gc-med text-foreground">Tags</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {(meta.tags && Array.isArray(meta.tags) && meta.tags.length > 0) ? (
                meta.tags.map((tag: string) => {
                  const config = getTagDisplayConfig(tag);
                  return (
                    <Badge
                      key={tag}
                      variant="outline"
                      className={cn("text-xs", config.className)}
                    >
                      {config.label}
                    </Badge>
                  );
                })
              ) : (
                <span className="text-sm text-muted-foreground">No tags selected</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ready State */}
      <Card className="border-maitai-green/20 bg-maitai-green/5">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="w-5 h-5 text-maitai-green mt-0.5" />
            <div className="space-y-1">
              <div className="text-sm font-gc-med text-foreground">Ready to Create</div>
              <div className="text-sm text-muted-foreground">
                Your dataset is ready to be created. Click the 'Create Dataset' button below to proceed.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DatasetReview;
